// Pure shaping for the Bob console: how loaded findings are filtered, grouped
// and related. No fetch and no React, so it unit-tests on its own.

import type { LedgerFinding } from "../api/codeReview";
import type { PeterIssue } from "../api/peterHandoff";
import { parseTs } from "./codeReviewFormat";
import { severityRank } from "./ledgerFormat";
import { parseBobRef } from "./peterHandoff";

export type SeverityGlyph = "octagon" | "triangle" | "circle";

export interface SeverityMeta {
  key: string;
  glyph: SeverityGlyph;
  /** Beside the severity group heading. */
  groupNote: string;
  /** Beside the severity filter. */
  filterNote: string;
  /** The severity axis in a finding's detail. */
  axis: string;
  /** Filled bars on the severity meter, out of 3. */
  level: number;
}

/** One entry per severity Bob writes, most serious first. */
export const SEVERITIES: readonly SeverityMeta[] = [
  { key: "BLOCK", glyph: "octagon", groupNote: "Must fix before release", filterNote: "release blocker", axis: "Blocks release", level: 3 },
  { key: "FLAG", glyph: "triangle", groupNote: "Should fix", filterNote: "should fix", axis: "Should fix", level: 2 },
  { key: "SUGGEST", glyph: "circle", groupNote: "Nice to have", filterNote: "nice to have", axis: "Nice to have", level: 1 },
];

export function severityMeta(severity: unknown): SeverityMeta | null {
  return SEVERITIES.find((s) => s.key === severity) ?? null;
}

/** Filled bars on the effort meter, out of 3. */
export const EFFORT_LEVELS: Readonly<Record<string, number>> = { Small: 1, Medium: 2, Large: 3 };

function severityOf(f: LedgerFinding): string {
  return typeof f.severity === "string" ? f.severity : "";
}

function createdMs(f: LedgerFinding): number {
  const t = parseTs(f.created_at);
  return Number.isFinite(t) ? t : -Infinity;
}

/** Worst first, then newest. Equal rows keep the order Bob sent them. */
export function sortWorstNewest(items: readonly LedgerFinding[]): LedgerFinding[] {
  return items.slice().sort((a, b) => {
    const bySeverity = severityRank(severityOf(a)) - severityRank(severityOf(b));
    if (bySeverity !== 0) return bySeverity;
    const at = createdMs(a);
    const bt = createdMs(b);
    return at === bt ? 0 : bt > at ? 1 : -1;
  });
}

/** Whole and from 1 up, like a GitHub pull request number. */
export function prOf(f: LedgerFinding): number | null {
  const pr: unknown = f.pr_number;
  return typeof pr === "number" && Number.isSafeInteger(pr) && pr > 0 ? pr : null;
}

export type FindingGroup =
  | { kind: "severity"; key: string; severity: string; findings: LedgerFinding[] }
  | { kind: "pr"; key: string; pr: number; findings: LedgerFinding[] }
  | { kind: "push"; key: string; sha: string | null; at: string | null; findings: LedgerFinding[] };

/** One group per severity, most serious first; a severity this build does not
 *  know gets its own group at the bottom rather than being dropped. */
export function groupBySeverity(items: readonly LedgerFinding[]): FindingGroup[] {
  const buckets = new Map<string, LedgerFinding[]>();
  for (const f of sortWorstNewest(items)) {
    const sev = severityOf(f);
    const bucket = buckets.get(sev);
    if (bucket) bucket.push(f);
    else buckets.set(sev, [f]);
  }
  return [...buckets.entries()]
    .sort((a, b) => severityRank(a[0]) - severityRank(b[0]))
    .map(([severity, findings]) => ({ kind: "severity", key: `sev:${severity}`, severity, findings }));
}

function text(v: unknown): string | null {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

/**
 * Where each finding came from: the pull request that carried it, else the
 * push it was first seen on. Groups run worst first, then newest. A linked
 * pull request leads, because a link is where the reader asked to start.
 */
export function groupByOrigin(items: readonly LedgerFinding[], linkedPr: number | null): FindingGroup[] {
  const groups = new Map<string, FindingGroup>();
  for (const f of sortWorstNewest(items)) {
    const pr = prOf(f);
    const sha = text(f.first_seen_sha);
    const key = pr !== null ? `pr:${pr}` : `push:${sha ?? ""}`;
    const existing = groups.get(key);
    if (existing) {
      existing.findings.push(f);
      continue;
    }
    groups.set(
      key,
      pr !== null
        ? { kind: "pr", key, pr, findings: [f] }
        : { kind: "push", key, sha, at: text(f.created_at), findings: [f] },
    );
  }
  const newest = (g: FindingGroup) => Math.max(...g.findings.map(createdMs));
  const all = [...groups.values()].sort((a, b) => {
    const bySeverity = severityRank(severityOf(a.findings[0]!)) - severityRank(severityOf(b.findings[0]!));
    if (bySeverity !== 0) return bySeverity;
    const an = newest(a);
    const bn = newest(b);
    if (an !== bn) return bn > an ? 1 : -1;
    if (a.kind === "pr" && b.kind === "pr") return b.pr - a.pr;
    return 0;
  });
  if (linkedPr === null) return all;
  const i = all.findIndex((g) => g.kind === "pr" && g.pr === linkedPr);
  return i <= 0 ? all : [all[i]!, ...all.slice(0, i), ...all.slice(i + 1)];
}

/** Case-insensitive match on the id, file, category, message and player impact. */
export function matchesSearch(f: LedgerFinding, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q === "") return true;
  const id = q.startsWith("#") ? q.slice(1) : q;
  if (/^\d+$/.test(id) && String(f.id) === id) return true;
  return [f.file, f.category, f.message, f.player_impact]
    .some((v) => typeof v === "string" && v.toLowerCase().includes(q));
}

/** Findings in the same file first, then the same category, never itself. */
export function relatedFindings(f: LedgerFinding, items: readonly LedgerFinding[], max = 5): LedgerFinding[] {
  const sameFile: LedgerFinding[] = [];
  const sameCategory: LedgerFinding[] = [];
  for (const other of sortWorstNewest(items)) {
    if (other.id === f.id) continue;
    if (typeof f.file === "string" && other.file === f.file) sameFile.push(other);
    else if (typeof f.category === "string" && f.category !== "" && other.category === f.category) sameCategory.push(other);
  }
  return [...sameFile, ...sameCategory].slice(0, max);
}

/** Loaded rows per category, most first, the name breaking a tie. */
export function categoryCounts(items: readonly LedgerFinding[]): Array<{ category: string; count: number }> {
  const counts = new Map<string, number>();
  for (const f of items) {
    if (typeof f.category !== "string" || f.category === "") continue;
    counts.set(f.category, (counts.get(f.category) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count || (a.category < b.category ? -1 : a.category > b.category ? 1 : 0));
}

/** The findings on `branch` that have a Peter issue filed from Bob. */
export function findingIdsSentToPeter(issues: readonly PeterIssue[], branch: string | undefined): Set<number> {
  const ids = new Set<number>();
  for (const issue of issues) {
    if (issue.source !== "bob") continue;
    const ref = parseBobRef(issue.source_ref);
    if (ref === null || (branch !== undefined && ref.base !== branch)) continue;
    ids.add(Number(ref.findingId));
  }
  return ids;
}

/** Bob's proposed replacement as lines. It is replacement text, not a diff. */
export function suggestionLines(f: LedgerFinding): string[] {
  const suggestion = typeof f.suggestion === "string" ? f.suggestion : "";
  if (suggestion.trim() === "") return [];
  return suggestion.replace(/\n$/, "").split("\n");
}

/** The repo's own name, without its owner. */
export function repoName(repo: string | undefined): string | null {
  if (typeof repo !== "string" || repo === "") return null;
  const i = repo.lastIndexOf("/");
  return i === -1 ? repo : repo.slice(i + 1);
}

/** `?finding=<id>` set (or removed, for null) on a search string, leaving every other parameter. */
export function searchWithFinding(search: string, id: number | null): string {
  const params = new URLSearchParams(search);
  if (id === null) params.delete("finding");
  else params.set("finding", String(id));
  const out = params.toString();
  return out === "" ? "" : `?${out}`;
}

/** The commit a finding's `file:line` is true at: the last pass that saw it,
 *  else the tracked branch. */
export function findingRef(f: LedgerFinding, branch: string | undefined): string | undefined {
  const sha = f.last_seen_sha;
  return typeof sha === "string" && sha.trim() !== "" ? sha : branch;
}

/** A GitHub blob url for a finding at `ref`, or null when there is no repo, ref or file. */
export function blobHref(repo: string | undefined, ref: string | undefined, f: LedgerFinding): string | null {
  if (!repo || !ref || !f.file) return null;
  // encodeURI keeps the slashes in a branch or a path; the line fragment goes on after.
  const url = encodeURI(`https://github.com/${repo}/blob/${ref}/${f.file}`);
  return f.line == null ? url : `${url}#L${f.line}`;
}
